import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Feature } from 'ol';
import { Observable } from 'rxjs';
import { NavaidModel, NavaidSitingToolModel } from 'src/app/models/navaid';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { PointsService } from 'src/app/services/points.service';
import { UtilsService } from 'src/app/services/utils.service';
import * as olProj from 'ol/proj';
import { ValidationCoordinatesDTO } from 'src/app/models/coord-dms';
import { CurvePointDTO } from 'src/app/models/curvePointDTO';
import Polygon from 'ol/geom/Polygon';
import { Draw } from 'ol/interaction';
import VectorSource from 'ol/source/Vector';
import olMap from 'ol/Map';
import { CalculationsService } from 'src/app/services/calculations.service';
import { MapService } from 'src/app/services/map.service';
import { CalculationSettingsModel, RNAVModel } from 'src/app/models/configuration';
import Circle from 'ol/geom/Circle';
import { Coordinate } from 'ol/coordinate';
import {fromCircle} from 'ol/geom/Polygon';
import { CoordenateService } from 'src/app/services/coordenate.service';
import { TabsService } from 'src/app/services/tabsService';
import { ProgressService } from 'src/app/services/progress.service';


@Component({
  selector: 'app-navaid-siting-tool',
  templateUrl: './navaid-siting-tool.component.html',
  styleUrls: ['../../../app.component.scss'],
  standalone: false
})
export class NavaidSitingToolComponent implements OnInit {


  draw: Draw;
  map: olMap;
  @Input() feature: Feature<any>;
  @Input() model: NavaidSitingToolModel;
  readonly forbiddenSubstringName = TabsService.SEPARATOR;
  constructor(public utilsService: UtilsService,
    private pointsService: PointsService,
    private bsModalRef: BsModalRef,
    private modalService: ModalControlService,
    private popupService: BsModalService,
    private mapService: MapService,
    private configurationService: ConfigurationService,
    private calculationService: CalculationsService,
    //private validationService : ValidationModalService
    private rcsControl: CoordenateService,
  ) { }

  ngOnInit(): void {
    if (this.model == null) {
      this.model = new NavaidSitingToolModel();
    }
    this.map = this.mapService.getMap('map');
  }

  setSubtendedAngleValue(minSubtendedAngleInserted: number) {
    if (minSubtendedAngleInserted < 0) {
      minSubtendedAngleInserted = 0;
    }
    if (minSubtendedAngleInserted > 90) {
      minSubtendedAngleInserted = 90;
    }
    this.model.minSubtendedAngle = minSubtendedAngleInserted;
    this.model.maxSubtendedAngle = 180 - minSubtendedAngleInserted;
  }

  validateAreaOfInterest(): Observable<boolean> {
    let validationCoordinates = new ValidationCoordinatesDTO();
    validationCoordinates.coordinates = this.model.coordinates;
    validationCoordinates.flightLevel = this.model.flightLevelMeters;
    return this.pointsService.postValidationCoordinates(validationCoordinates);
  }

  openSitingToolArea(model: NavaidSitingToolModel): void {
    if (this.draw != null) {
      this.map.removeInteraction(this.draw);
    }
    const source = new VectorSource();
    let type: any = model.type;
    let freeHand: boolean = false;
    if (model.type == "Freehand") {
      freeHand = true;
      type = "Polygon";
    }
    this.draw = new Draw({
      source: source,
      type: type,
      freehand: freeHand,
    });
    this.map.addInteraction(this.draw);
    let context = this;
    this.draw.on('drawend', function (endEvent) {
      var interceptedNavaids: NavaidModel[] = [];
      var geometry = endEvent.feature.getGeometry();
      var extent = geometry.getExtent();
      context.pointsService.navaidsLayers.childLayers.forEach(child => {
        child.layers.forEach(layer => {
          if (layer.visible) {
            layer.olLayer.getSource().forEachFeatureIntersectingExtent(extent, function (feature) {
              if (geometry.intersectsExtent(feature.getGeometry().getExtent())) {
                interceptedNavaids.push(context.pointsService.transformFeatureInNavaid(feature));
              }
            });
          }
        })
      });
      var isInterceptingNavaids: Boolean = false;
      interceptedNavaids.forEach(navaid => {
        let existNavaid = model.navaids.findIndex(el => el.id == navaid.id && el.type == navaid.navaidtype);
        if (existNavaid > -1) {
          isInterceptingNavaids = true;
          context.modalService.showMesssageModal("Siting Tool", "The siting tool area can not contain selected navaids. \nPlease start the siting tool again and redraw the siting tool area.");
          return;
        }
      });
      context.map.removeInteraction(context.draw);
      context.draw = null;
      if (!isInterceptingNavaids) {
        let bsModalRef: BsModalRef = context.popupService.show(NavaidSitingToolComponent);
        bsModalRef.content.feature = endEvent.feature;
        bsModalRef.content.model = model;
      };
    });
  }

  calculate(): void {

    let coordinates: any[];

    if (this.feature.getGeometry() instanceof Circle) {

      // Circle
      let circlePolygon: Polygon = fromCircle(this.feature.getGeometry());
      coordinates = circlePolygon.getCoordinates()[0];

    } else {
      // Polygon or freehand
      coordinates = this.feature.getGeometry().getCoordinates()[0];
    }

    coordinates.forEach(coordinate => {
      let coord: CurvePointDTO = CurvePointDTO.mapLonLatCoordinatesToService(olProj.toLonLat(coordinate,this.rcsControl.activeRCS.isactive? this.rcsControl.activeRCS.epsg : "EPSG:3857"));
      if (this.model.coordinates.findIndex(x => x.latitude == coord.latitude && x.longitude === coord.longitude) == -1) {
        this.model.coordinates.push(coord);
      }
    });

    // Validate Area of interest
    if (this.model.visibilityAnalysis) {
      this.validateAreaOfInterest().subscribe(res => {
        if (!res) {
          this.modalService.showMesssageModal("Siting Tool", "The flight level is below the terrain in at least one of the selected area vertices. \nPlease start the siting tool again and redraw the siting tool area");
        }
      });
    }

    this.model.calculationSettings = CalculationSettingsModel.mapToCalculation(this.configurationService.currentConfiguration);
    this.model.rnav = RNAVModel.mapToCalculation(this.configurationService.currentConfiguration.rnav);
    this.calculationService.calculateNavaidSitingTool(this.model, true);
    this.bsModalRef.hide();
  }

  redraw(): void {
    this.openSitingToolArea(this.model);
    this.bsModalRef.hide();
  }

  cancel(): void {
    this.bsModalRef.hide();
  }
}
